import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  MenuItem,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import MDBox from "components/MDBox";
import { createAudit, updateAudit } from "app/services/iqms.service";

const AUDIT_TYPES = ["INTERNAL", "EXTERNAL", "SUPPLIER", "CUSTOMER", "COMPLIANCE"];
const AUDIT_STATUSES = ["SCHEDULED", "IN_PROGRESS", "COMPLETED", "CANCELLED", "PENDING_REVIEW"];

const initialData = {
  id: null,
  auditCode: "",
  auditTitle: "",
  auditType: "INTERNAL",
  description: "",
  auditor: "",
  auditee: "",
  scheduledDate: "",
  status: "SCHEDULED",
};

const AddAuditDialog = ({ open, setOpen, onSuccess, editObject }) => {
  const [data, setData] = useState(initialData);
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editObject) {
      setData({
        ...editObject,
        scheduledDate: editObject.scheduledDate?.substring(0, 10) || "",
      });
    } else {
      setData(initialData);
    }
    setMessageOpen(false);
  }, [editObject, open]);

  const validateForm = () => {
    if (!data.auditCode || data.auditCode.trim() === "") {
      setError("Audit code is required");
      return false;
    }
    if (!data.auditTitle || data.auditTitle.trim() === "") {
      setError("Audit title is required");
      return false;
    }
    if (!data.scheduledDate) {
      setError("Scheduled date is required");
      return false;
    }
    return true;
  };

  const setError = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccess = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (editObject) {
        await updateAudit(data.id, data);
        setSuccess("Audit updated successfully");
      } else {
        await createAudit(data);
        setSuccess("Audit created successfully");
      }
      setTimeout(() => {
        setData(initialData);
        setMessageOpen(false);
        setOpen(false);
        onSuccess && onSuccess();
      }, 1000);
    } catch (error) {
      setError(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setData(initialData);
    setMessageOpen(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editObject ? "Edit Audit" : "Schedule New Audit"}</DialogTitle>
      <DialogContent>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="auditCode"
            label="Audit Code"
            fullWidth
            variant="outlined"
            value={data.auditCode}
            onChange={(e) => setData({ ...data, auditCode: e.target.value })}
            required
          />
          <TextField
            id="auditType"
            label="Audit Type"
            fullWidth
            select
            variant="outlined"
            value={data.auditType}
            onChange={(e) => setData({ ...data, auditType: e.target.value })}
          >
            {AUDIT_TYPES.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="auditTitle"
            label="Audit Title"
            fullWidth
            variant="outlined"
            value={data.auditTitle}
            onChange={(e) => setData({ ...data, auditTitle: e.target.value })}
            required
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="auditor"
            label="Auditor"
            fullWidth
            variant="outlined"
            value={data.auditor}
            onChange={(e) => setData({ ...data, auditor: e.target.value })}
          />
          <TextField
            id="auditee"
            label="Auditee"
            fullWidth
            variant="outlined"
            value={data.auditee}
            onChange={(e) => setData({ ...data, auditee: e.target.value })}
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="scheduledDate"
            label="Scheduled Date"
            type="date"
            fullWidth
            variant="outlined"
            value={data.scheduledDate}
            onChange={(e) => setData({ ...data, scheduledDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            id="status"
            label="Status"
            fullWidth
            select
            variant="outlined"
            value={data.status}
            onChange={(e) => setData({ ...data, status: e.target.value })}
          >
            {AUDIT_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>{status.replace("_", " ")}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="description"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={data.description}
            onChange={(e) => setData({ ...data, description: e.target.value })}
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            severity={severity}
            action={
              <IconButton size="small" onClick={() => setMessageOpen(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained">
          {editObject ? "Update" : "Schedule"}
        </Button>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddAuditDialog);

